import * as Accordion from '@radix-ui/react-accordion';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as RadioPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import type { IconName } from '../Icon/icons';

export const listItem = cva(
  `inline-flex relative focus-within:ring-3 focus-within:z-10`,
  {
    variants: {
      width: { full: 'w-full', fit: 'w-fit' },
      grouping: {
        joined:
          'last:border-b last:border-x first:border-t first:border-x border-x border-b first:rounded-t-m last:rounded-b-m',
        noSeparators:
          'border-x first:border-t last:border-b first:rounded-t-m last:rounded-b-m',
        separated: 'border rounded-m',
      },
      interaction: {
        hover: 'cursor-pointer hover:bg-secondary-hover',
        active:
          'cursor-pointer hover:bg-secondary-hover active:bg-secondary-active',
        disabled: 'aria-disabled:bg-surface-disabled',
      },
      disabled: {
        true: 'bg-surface-disabled text-content-disabled cursor-not-allowed pointer-events-none',
      },
      background: {
        white: 'bg-surface',
      },
    },
    defaultVariants: {
      grouping: 'joined',
    },
  },
);

type BaseProps = {
  children: ReactNode;
  interactionType: 'accordion' | 'radio' | 'checkbox' | 'static';
};

/**
 * *** Props ***
 *
 * `defaultValue` [string] -- if defaultValue === listItem.value, the accordion will be expanded
 * on that list item by default. combine this with `ListItem collapsible={false}` for a ListItem that
 * is always expanded by default.
 *
 * `type` ['single' | 'multiple'] -- determines the behaviour of the List and its children; if `type === 'single'`,
 * expanding one item will close the currently expanded one; if `type === 'multiple'`, each item will be
 * individually expandable at the same time.
 *
 */
type AccordionListProps = {
  interactionType: 'accordion';
} & (Accordion.AccordionSingleProps | Accordion.AccordionMultipleProps);

/**
 * *** Props ***
 *
 * `defaultValue` [string | number] -- if defaultValue === listItem.value, the radio option will be selected by default.
 *
 */
type RadioListProps = {
  interactionType: 'radio';
  defaultValue?: string | number;
} & RadioPrimitive.RadioGroupProps;

/**
 * *** Props ***
 *
 * `defaultValue` is not supported for checkbox lists. Instead, use the `checked` prop on each checkbox item.
 *
 */
type CheckboxListProps = {
  interactionType: 'checkbox';
};

export type ListPropsRadio = BaseProps &
  RadioListProps & {
    onChange?: RadioPrimitive.RadioGroupProps['onValueChange'];
  } & (
    | { 'aria-label': string; 'aria-labelledby'?: never }
    | { 'aria-label'?: never; 'aria-labelledby': string }
  );

export type ListPropsCheckbox = BaseProps &
  CheckboxListProps & {
    'aria-labelledby'?: string;
  };

type StaticListProps = {
  interactionType: 'static';
  children: ReactNode;
};

export type ListWrapperProps =
  ListPropsAccordion | ListPropsRadio | ListPropsCheckbox | StaticListProps;

export type ListPropsAccordion = BaseProps & AccordionListProps;

export type ListGrouping = VariantProps<typeof listItem>['grouping'];

export type ListProps = (
  ListPropsRadio | ListPropsAccordion | ListPropsCheckbox | StaticListProps
) & {
  loading?: boolean;
  grouping?: ListGrouping;
};

/**
 * *** Props ***
 *
 * `collapsible` [boolean] -- determines whether to show the chevron expander button
 */
export type ListItemBaseProps = VariantProps<typeof listItem> & {
  heading: string;
  body?: string | ReactNode;
  linkTo?: string;
  leadingElement?: ReactNode;
  headingTag?: string;
  children?: ReactNode;
  collapsible?: boolean;
  required?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
};

export type ListItemProps = Pick<
  ListItemBaseProps,
  | 'heading'
  | 'body'
  | 'linkTo'
  | 'leadingElement'
  | 'headingTag'
  | 'disabled'
  | 'onClick'
> &
  VariantProps<typeof listItem> & {
    external?: boolean;
    trailingIcon?: IconName;
  };

export type RadioListItemProps = ListItemBaseProps &
  Omit<RadioPrimitive.RadioGroupItemProps, 'checked'>;

export type CheckboxListItemProps = ListItemBaseProps &
  CheckboxPrimitive.CheckboxProps & {
    onChange?: CheckboxPrimitive.CheckboxProps['onCheckedChange'];
    checked?: boolean;
    defaultChecked?: boolean;
    indeterminate?: boolean;
  };

export type AccordionListItemProps = ListItemBaseProps &
  Accordion.AccordionItemProps & {
    trailingElement?: ReactNode;
    trailingElementCentered?: boolean;
  };

export const trailingElementWrapper = cva('ml-auto', {
  variants: {
    centered: {
      true: 'my-auto',
      false: 'mt-[2px] mb-auto',
    },
  },
});
